import ih from 'immutability-helper';
import { get, omit as _omit, cloneDeep as _cloneDeep } from 'lodash';
import uuid from 'uuid/v4';

import store from 'store';
import { SELECT_PROJECT } from '../../services/project/actionTypes';
import * as types from './actionTypes';
import { getCanvasMaxLeftPosition } from './selectors';
import initialState from './initialState';

export default (state = _cloneDeep(initialState), action = {}) => {
  switch (action.type) {
    case types.ADD_CANVAS:
      return ih(state, {
        data: {
          $merge: {
            [action.payload.canvasId]: {
              resources: [],
              connections: [],
            },
          },
        },
      });
    case types.DELETE_CANVAS:
      return ih(state, {
        data: { $set: _omit(state.data, [action.payload.canvasId]) },
      });
    case types.ADD_RESOURCE_TO_CANVAS: {
      return ih(state, {
        data: {
          $apply: data => {
            const {
              payload: { canvasId, resourceId, resourceType, resourceUUID },
            } = action;
            if (
              !data[canvasId] ||
              data[canvasId].resources.find(({ id }) => id === resourceId)
            ) {
              return data;
            }

            const leftPos = getCanvasMaxLeftPosition(canvasId)(
              store.getState(),
            );

            return ih(data, {
              $merge: {
                [canvasId]: {
                  ...data[canvasId],
                  resources: [
                    ...data[canvasId].resources,
                    {
                      id: resourceId,
                      type: resourceType,
                      // in case that there is no resourceUUID we should create one
                      uuid: resourceUUID || uuid(),
                      position: {
                        left: `${leftPos}px`,
                        top: '100px',
                      },
                    },
                  ],
                },
              },
            });
          },
        },
      });
    }
    case types.DELETE_RESOURCE_FROM_CANVAS: {
      return ih(state, {
        data: {
          $apply: data => {
            const { canvasId, resourceId } = action.payload;

            if (
              !data[canvasId] ||
              !data[canvasId].resources.some(({ id }) => id === resourceId)
            ) {
              return data;
            }

            const resourceUUID = data[canvasId].resources.find(
              resource => resource.id === resourceId,
            ).uuid;

            const connectedTargetIds = data[canvasId].connections.reduce(
              (acc, curr) =>
                curr.sourceId === resourceUUID ? [...acc, curr.targetId] : acc,
              [],
            );

            return ih(data, {
              [canvasId]: {
                resources: {
                  $set: data[canvasId].resources.filter(
                    resource =>
                      resource.id !== resourceId &&
                      !connectedTargetIds.includes(resource.uuid),
                  ),
                },
                connections: {
                  $set: data[canvasId].connections.filter(
                    connection =>
                      connection.sourceId !== resourceUUID &&
                      connection.targetId !== resourceUUID,
                  ),
                },
              },
            });
          },
        },
      });
    }
    case types.DELETE_RESOURCE_FROM_ALL_CANVASES: {
      const resourceId = get(action, ['payload', 'resourceID'], 'undefined');
      const data = get(state, 'data', {});

      const iterate = (accumulator, key) => {
        const canvas = data[key];

        const resource = canvas.resources.find(
          ({ id }) => id === resourceId,
        ) || { uuid: 'undefined' };

        const closeRelatedResources = resources => {
          if (resource.type !== 'target') {
            return resources;
          }

          const relatedActionsUUIds = canvas.connections
            .filter(({ sourceId }) => sourceId === resource.uuid)
            .map(({ targetId }) => targetId);

          return resources.filter(
            ({ uuid }) => !relatedActionsUUIds.includes(uuid),
          );
        };

        const closeResource = canvas =>
          canvas.resources.filter(({ id }) => id !== resource.id);

        return {
          ...accumulator,
          [key]: {
            ...canvas,
            resources: closeRelatedResources(closeResource(canvas)),
            connections: canvas.connections.filter(
              ({ sourceId, targetId }) =>
                typeof resource.uuid !== 'undefined' &&
                sourceId !== resource.uuid &&
                targetId !== resource.uuid,
            ),
          },
        };
      };

      const removeResourceFromData = (resourceId, data = {}) =>
        Object.keys(data).reduce(iterate, {});

      const nextState = {
        ...state,
        data: removeResourceFromData(resourceId, data),
      };

      return nextState;
    }
    case types.UPDATE_RESOURCES_OF_CANVAS: {
      return ih(state, {
        data: {
          $apply: data => {
            const {
              payload: { canvasId, resources },
            } = action;
            if (!data[canvasId]) {
              return data;
            }
            return ih(data, {
              $merge: {
                [canvasId]: {
                  ...data[canvasId],
                  resources,
                },
              },
            });
          },
        },
      });
    }
    case types.ADD_CONNECTION_TO_CANVAS: {
      return ih(state, {
        data: {
          $apply: data => {
            const {
              payload: { canvasId, sourceId, targetId },
            } = action;

            if (
              !data[canvasId] ||
              data[canvasId].connections.some(
                connection =>
                  connection.sourceId === sourceId &&
                  connection.targetId === targetId,
              )
            ) {
              return data;
            }

            return ih(data, {
              $merge: {
                [canvasId]: {
                  ...data[canvasId],
                  connections: [
                    ...data[canvasId].connections,
                    {
                      sourceId,
                      targetId,
                    },
                  ],
                },
              },
            });
          },
        },
      });
    }
    case types.DELETE_ALL_CONNECTIONS_FROM_CANVAS: {
      return ih(state, {
        data: {
          $apply: data => {
            const { canvasId } = action.payload;

            // get the ids of resources connected
            const connectedTargetIds = data[canvasId].connections.map(
              connection => connection.targetId,
            );

            return ih(data, {
              [canvasId]: {
                resources: {
                  $set: data[canvasId].resources.filter(
                    resource => !connectedTargetIds.includes(resource.uuid),
                  ),
                },
                connections: {
                  $set: [],
                },
              },
            });
          },
        },
      });
    }
    case types.CLEAR_CANVAS:
      return ih(state, {
        data: {
          $merge: {
            [action.payload.canvasId]: {
              resources: [],
              connections: [],
            },
          },
        },
      });
    case SELECT_PROJECT:
      return ih(state, {
        $set: initialState,
      });
    default:
      return state;
  }
};
